import { useEffect, useState, useMemo } from "react";
import { FileText, Loader2, Copy, Printer, Download, Sparkles, Wand2, CheckCircle2, Circle, FileType2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const DISCIPLINES = [
  "All events",
  "Barrel Racing", "Bareback", "Saddle Bronc", "Bull Riding",
  "Tie-Down Roping", "Steer Wrestling", "Breakaway Roping",
  "Team Roping (Header)", "Team Roping (Heeler)",
];

export default function Presenter() {
  const [athletes, setAthletes] = useState([]);
  const [discipline, setDiscipline] = useState("Barrel Racing");
  const [selected, setSelected] = useState([]);
  const [event, setEvent] = useState("");
  const [when, setWhen] = useState("");
  const [events, setEvents] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [building, setBuilding] = useState(false);
  const [q, setQ] = useState("");
  const [scan, setScan] = useState(null);
  const [draw, setDraw] = useState(null);
  const [filling, setFilling] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.listAthletes({}).then(setAthletes);
    api.listEvents().then(setEvents).catch(() => {});
    const t = setInterval(() => {
      api.listAthletes({}).then(setAthletes).catch(() => {});
      api.scanStatus().then(setScan).catch(() => {});
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const byId = useMemo(() => Object.fromEntries(athletes.map((a) => [a.id, a])), [athletes]);
  const unscanned = selected.filter((id) => byId[id] && byId[id].discovery_status !== "done");
  const scanning = scan && (scan.queued > 0 || scan.current);

  const gather = async () => {
    if (unscanned.length === 0) return toast.info("All selected contestants already have intel");
    const r = await api.scanAthletes(unscanned);
    toast.success(`Gathering talking points for ${r.enqueued} contestant(s) in the background`);
    api.scanStatus().then(setScan);
  };

  const allEvents = discipline === "All events";
  const pool = useMemo(() => {
    return athletes
      .filter((a) => allEvents || (a.disciplines || []).includes(discipline))
      .filter((a) => !q || a.known_name.toLowerCase().includes(q.toLowerCase()));
  }, [athletes, discipline, q, allEvents]);

  const toggle = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const autoFill = async () => {
    if (!event) return toast.error("Pick an event to bind the draw first");
    setFilling(true);
    try {
      const d = await api.presenterDraw(event, discipline);
      setDraw(d);
      setSelected(d.matched_ids);
      if (d.total === 0) toast.info(`No ${discipline} draw found for ${event}`);
      else if (d.matched_ids.length === 0) toast.info(`Found ${d.total} in the draw but none matched an athlete profile`);
      else toast.success(`Loaded ${d.matched_ids.length} of ${d.total} drawn ${discipline} contestants`);
    } catch {
      toast.error("Could not load the draw");
    }
    setFilling(false);
  };

  const exportDocx = async () => {
    if (!sheet) return;
    setExporting(true);
    try {
      const blob = await api.presenterExportDocx(sheet);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${sheet.discipline.replace(/\s+/g, "-")}-run-sheet.docx`;
      a.click();
      toast.success("Word run sheet downloaded");
    } catch {
      toast.error("Could not export Word file");
    }
    setExporting(false);
  };

  const build = async () => {
    if (selected.length === 0) return toast.error("Select at least one contestant");
    setBuilding(true);
    try {
      const r = await api.presenterBuild({
        discipline, athlete_ids: selected, event: event || null,
        performance: "PERFORMANCE: #1", when,
      });
      setSheet(r);
    } catch {
      toast.error("Could not build sheet");
    }
    setBuilding(false);
  };

  const asText = () => {
    if (!sheet) return "";
    let out = `${sheet.discipline}\n${sheet.performance}\n${sheet.when || ""}\n\n`;
    const groups = sheet.groups && sheet.groups.length ? sheet.groups : [{ discipline: sheet.discipline, contestants: sheet.contestants || [] }];
    groups.forEach((g) => {
      if (groups.length > 1) out += `\n=== ${g.discipline} ===\n\n`;
      g.contestants.forEach((c) => {
        out += `${c.position} ${c.name}\n`;
        (c.notes || []).forEach((n) => (out += `** ${n}\n`));
        if (c.brief) {
          if (c.brief.five_things?.length) { out += `5 THINGS TO KNOW\n`; c.brief.five_things.forEach((t) => (out += `  • ${t}\n`)); }
          if (c.brief.starters?.length) { out += `CONVERSATION STARTERS\n`; c.brief.starters.forEach((t) => (out += `  • ${t}\n`)); }
          if (c.brief.questions?.length) { out += `QUESTIONS TO ASK\n`; c.brief.questions.forEach((t) => (out += `  • ${t}\n`)); }
        }
        c.standings.forEach((s) => (out += `${s}\n`));
        c.bio.forEach((b) => (out += `${b}\n`));
        if (c.hometown) out += `${c.hometown}\n`;
        if (c.animal) {
          out += `${c.animal.label}  (${c.animal.contractor})\n`;
          (c.animal.notes || []).forEach((n) => n && (out += `  - ${n}\n`));
        }
        out += `\n`;
      });
    });
    return out;
  };

  const copy = () => { navigator.clipboard.writeText(asText()); toast.success("Copied to clipboard"); };
  const download = () => {
    const blob = new Blob([asText()], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${sheet.discipline.replace(/\s+/g, "-")}-run-sheet.txt`;
    a.click();
  };

  return (
    <div className="max-w-[1700px] mx-auto px-5 md:px-8 py-8">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E8833A] mb-2">Presenter Tools</div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-[#F3EFE6] leading-[1.05]">
          Build a <span className="text-[#E8833A]">run sheet.</span>
        </h1>
        <p className="text-[#B8BAC4] text-base mt-3 max-w-2xl">
          Pick an event and the contestants in draw order. We assemble their standings, talking points,
          hometown and drawn stock into an announcer-ready sheet you can copy, print or download.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* Builder */}
        <div className="rounded-xl border border-[#262933] bg-[#12141A] p-5 h-fit">
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#686B77]">Event</label>
          <div className="flex flex-wrap gap-1.5 mt-2 mb-4">
            {DISCIPLINES.map((d) => (
              <button
                key={d}
                data-testid={`presenter-discipline-${d.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                onClick={() => setDiscipline(d)}
                className={cn("px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider border transition-colors",
                  discipline === d ? "bg-[#E8833A] text-[#0B0D11] border-[#E8833A]" : "bg-[#1A1D26] text-[#9A9CA6] border-[#262933] hover:text-[#F3EFE6]")}
              >
                {d}
              </button>
            ))}
          </div>

          <label className="font-mono text-[10px] uppercase tracking-widest text-[#686B77]">Bind draw to event (optional)</label>
          <select
            data-testid="presenter-event-select"
            value={event}
            onChange={(e) => setEvent(e.target.value)}
            className="w-full mt-1 mb-3 bg-[#0F1116] border border-[#262933] rounded-lg px-3 py-2 text-sm text-[#F3EFE6] focus:outline-none focus:ring-1 focus:ring-[#E8833A]"
          >
            <option value="">Any / none</option>
            {events.map((e) => <option key={e.name} value={e.name.replace("2025 ", "").replace("Canadian Finals Rodeo", "CFR 2025")}>{e.name}</option>)}
            <option value="Maple Leaf Circuit Finals 2025">Maple Leaf Circuit Finals 2025</option>
            <option value="CFR 2025">CFR 2025</option>
          </select>

          <button
            data-testid="presenter-autofill-button"
            onClick={autoFill}
            disabled={filling || !event || allEvents}
            className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-[#1A1D26] border border-[#E8833A]/40 text-[#F3EFE6] hover:bg-[#22262F] transition-colors disabled:opacity-50"
            title={allEvents ? "Pick a single event to auto-fill a draw" : !event ? "Pick an event above first" : ""}
          >
            {filling ? <Loader2 className="h-4 w-4 animate-spin text-[#E8833A]" /> : <Wand2 className="h-4 w-4 text-[#E8833A]" />}
            Auto-fill draw order
          </button>

          {draw && draw.entries.length > 0 && (
            <div data-testid="draw-preview" className="mb-4 rounded-lg border border-[#262933] bg-[#0F1116] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#686B77]">
                  {draw.discipline} draw · {draw.matched}/{draw.total} matched
                </span>
                <button onClick={() => setDraw(null)} className="text-[10px] text-[#E8833A] font-mono">hide</button>
              </div>
              <div className="max-h-[200px] overflow-auto space-y-1 pr-1">
                {draw.entries.map((e, i) => (
                  <div key={i} data-testid="draw-entry" className="flex items-center gap-2 text-xs">
                    {e.athlete_id ? (
                      e.has_intel ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-[#4ADE80] shrink-0" />
                      ) : (
                        <Circle className="h-3.5 w-3.5 text-[#FACC15] shrink-0" />
                      )
                    ) : (
                      <Circle className="h-3 w-3 text-[#686B77] shrink-0" />
                    )}
                    <span className={cn("truncate", e.athlete_id ? "text-[#F3EFE6]" : "text-[#686B77] line-through")}>
                      {e.contestant}
                    </span>
                    {e.animal?.label && <span className="font-mono text-[9px] text-[#686B77] truncate ml-auto">{e.animal.label}</span>}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-[#686B77] mt-2 leading-relaxed">
                <span className="text-[#4ADE80]">●</span> has intel &nbsp;
                <span className="text-[#FACC15]">●</span> needs a scan &nbsp;
                <span className="text-[#686B77]">●</span> no profile
              </p>
            </div>
          )}

          <label className="font-mono text-[10px] uppercase tracking-widest text-[#686B77]">Date / time line</label>
          <Input value={when} onChange={(e) => setWhen(e.target.value)} placeholder="6:00PM FRI AUG 21 2026"
            className="mt-1 mb-4 bg-[#0F1116] border-[#262933] text-[#F3EFE6] focus-visible:ring-[#E8833A]" data-testid="presenter-when-input" />

          <div className="flex items-center justify-between mb-2">
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#686B77]">Contestants ({selected.length} picked)</label>
            {selected.length > 0 && <button onClick={() => setSelected([])} className="text-[10px] text-[#E8833A] font-mono">clear</button>}
          </div>

          {selected.length > 0 && (
            <div data-testid="selected-tray" className="mb-3 flex flex-wrap gap-1.5 rounded-lg border border-[#E8833A]/30 bg-[#E8833A]/5 p-2">
              {selected.map((id, i) => {
                const a = byId[id];
                if (!a) return null;
                return (
                  <button
                    key={id}
                    data-testid="selected-chip"
                    onClick={() => toggle(id)}
                    title="Remove"
                    className="group flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-md bg-[#1A1D26] border border-[#E8833A]/40 text-[#F3EFE6] text-xs hover:border-[#E8833A] transition-colors"
                  >
                    <span className="font-mono text-[10px] text-[#E8833A]">{i + 1}</span>
                    <span className="truncate max-w-[130px]">{a.known_name}</span>
                    <X className="h-3 w-3 text-[#686B77] group-hover:text-[#E8833A]" />
                  </button>
                );
              })}
            </div>
          )}

          <div className="relative mb-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any athlete…"
              className="h-9 bg-[#0F1116] border-[#262933] text-[#F3EFE6] focus-visible:ring-[#E8833A]" data-testid="presenter-filter" />
          </div>
          <div className="max-h-[360px] overflow-auto pr-1 space-y-1">
            {pool.map((a) => {
              const idx = selected.indexOf(a.id);
              return (
                <button
                  key={a.id}
                  data-testid="presenter-athlete-option"
                  onClick={() => toggle(a.id)}
                  className={cn("w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-left transition-colors",
                    idx > -1 ? "border-[#E8833A]/50 bg-[#E8833A]/10" : "border-[#262933] bg-[#0F1116] hover:border-[#3a3d48]")}
                >
                  <span className="text-sm text-[#F3EFE6] truncate">{a.known_name}</span>
                  {idx > -1 ? (
                    <span className="h-5 w-5 rounded-full bg-[#E8833A] text-[#0B0D11] text-[11px] font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                  ) : (
                    <span className="text-[10px] text-[#686B77] font-mono shrink-0">{allEvents ? (a.disciplines || [])[0] : a.hometown}</span>
                  )}
                </button>
              );
            })}
            {pool.length === 0 && <div className="text-[#686B77] text-sm py-6 text-center">No contestants in this event</div>}
          </div>

          <button
            data-testid="presenter-build-button"
            onClick={build}
            disabled={building}
            className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-sm bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors disabled:opacity-60"
          >
            {building ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            Build run sheet
          </button>

          {selected.length > 0 && (
            <button
              data-testid="presenter-gather-button"
              onClick={gather}
              disabled={scanning || unscanned.length === 0}
              className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm bg-[#1A1D26] border border-[#E8833A]/40 text-[#F3EFE6] hover:bg-[#22262F] transition-colors disabled:opacity-60"
            >
              {scanning ? <Loader2 className="h-4 w-4 animate-spin text-[#E8833A]" /> : <Sparkles className="h-4 w-4 text-[#E8833A]" />}
              {scanning
                ? `Gathering… ${scan.processed}/${scan.total}${scan.current_name ? ` · ${scan.current_name}` : ""}`
                : unscanned.length === 0
                ? "All contestants have intel"
                : `Gather talking points (${unscanned.length} to scan)`}
            </button>
          )}
          {scanning && (
            <p className="text-[11px] text-[#686B77] mt-2 text-center">Runs in the background — rebuild the sheet when done to see the new talking points.</p>
          )}
        </div>

        {/* Output */}
        <div className="rounded-xl border border-[#262933] bg-[#0F1116] p-6 min-h-[400px]">
          {!sheet ? (
            <div className="flex flex-col items-center justify-center h-full py-20 text-center">
              <FileText className="h-8 w-8 text-[#686B77] mb-3" />
              <p className="text-[#9A9CA6]">Pick an event and contestants, then build your sheet.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-end gap-2 mb-4">
                <SheetBtn onClick={copy} icon={Copy} label="Copy" testid="presenter-copy" />
                <SheetBtn onClick={() => window.print()} icon={Printer} label="Print" testid="presenter-print" />
                <SheetBtn onClick={exportDocx} icon={exporting ? Loader2 : FileType2} label="Word" testid="presenter-word" spin={exporting} />
                <SheetBtn onClick={download} icon={Download} label="Text" testid="presenter-download" />
              </div>
              <div id="run-sheet" data-testid="run-sheet" className="bg-[#12141A] rounded-lg p-6 border border-[#262933]">
                <h2 className="font-display font-extrabold text-2xl text-[#F3EFE6]">{sheet.discipline}</h2>
                <div className="font-mono text-xs text-[#9A9CA6] mt-1">{sheet.performance}{sheet.when ? ` · ${sheet.when}` : ""}</div>
                <div className="mt-5 space-y-8">
                  {(sheet.groups && sheet.groups.length ? sheet.groups : [{ discipline: sheet.discipline, contestants: sheet.contestants || [] }]).map((g) => (
                    <div key={g.discipline} data-testid="run-sheet-group">
                      {sheet.groups && sheet.groups.length > 1 && (
                        <div className="font-display font-bold text-xl text-[#E8833A] border-b border-[#E8833A]/30 pb-1 mb-3" data-testid="run-sheet-group-heading">{g.discipline}</div>
                      )}
                      <div className="space-y-6">
                        {g.contestants.map((c) => (
                          <div key={c.athlete_id} data-testid="run-sheet-contestant" className="pb-5 border-b border-[#1E212A] last:border-0">
                            <div className="font-display font-bold text-lg text-[#F3EFE6]">
                              <span className="text-[#E8833A]">{c.position}</span> {c.name}
                            </div>
                            {(c.notes || []).map((n, i) => (
                              <div key={i} data-testid="sheet-note" className="text-sm font-semibold text-[#E8833A] flex items-start gap-1.5">
                                <span>★</span> {n}
                              </div>
                            ))}
                            {c.brief && (
                              <div className="mt-2 space-y-2" data-testid="sheet-brief">
                                {c.brief.five_things?.length > 0 && (
                                  <div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#E8833A]">5 Things To Know</div>
                                    {c.brief.five_things.map((t, i) => <div key={i} className="text-sm text-[#D8DAE2]">• {t}</div>)}
                                  </div>
                                )}
                                {c.brief.starters?.length > 0 && (
                                  <div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#4ADE80]">Conversation Starters</div>
                                    {c.brief.starters.map((t, i) => <div key={i} className="text-sm text-[#D8DAE2]">• {t}</div>)}
                                  </div>
                                )}
                                {c.brief.questions?.length > 0 && (
                                  <div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#7FB2E0]">Questions To Ask</div>
                                    {c.brief.questions.map((t, i) => <div key={i} className="text-sm text-[#D8DAE2]">• {t}</div>)}
                                  </div>
                                )}
                              </div>
                            )}
                            {c.standings.map((s, i) => <div key={i} className="text-sm text-[#B8BAC4]">{s}</div>)}
                            {c.bio.map((b, i) => <div key={i} className="text-sm text-[#9A9CA6]">• {b}</div>)}
                            {c.hometown && <div className="text-xs font-mono text-[#686B77] mt-1">{c.hometown}</div>}
                            {c.animal && (
                              <div className="mt-2 rounded-lg bg-[#1A1D26] border border-[#262933] px-3 py-2">
                                <div className="text-sm font-semibold text-[#E8833A]">{c.animal.label}
                                  <span className="text-[#9A9CA6] font-normal"> · {c.animal.contractor}</span>
                                </div>
                                {(c.animal.notes || []).filter(Boolean).map((n, i) => (
                                  <div key={i} className="text-xs text-[#B8BAC4] mt-0.5">— {n}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const SheetBtn = ({ onClick, icon: Icon, label, testid, spin }) => (
  <button onClick={onClick} data-testid={testid}
    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#1A1D26] border border-[#262933] text-[#F3EFE6] hover:border-[#E8833A]/50 transition-colors">
    <Icon className={cn("h-3.5 w-3.5", spin && "animate-spin")} /> {label}
  </button>
);
